<script setup lang="ts">
import { systemCountries } from "@/package/global-constants/system-countries";
import { UiChip, UiInput, UiModal } from "@packages/component-library";
import { computed, onBeforeMount, onMounted, ref, watch } from "vue";
import { IconSearchRegular } from "@packages/icon-library";
import {
  getSystemCountry,
  selectUserCity,
} from "@/package/global-helpers/system-country";
import { userLocationStore } from "@/modules/user-location";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["close"]);

const { t } = useI18n();
const locationStore = userLocationStore();

const showModal = ref(false);
const search = ref("");
const selectedCountry = ref("");
const searchStatus = computed(() => locationStore.citiesSearchStatus);
const searchResults = computed(() => locationStore.citiesSearchResults);

const topCities = computed(() =>
  locationStore.getCitiesByCountryId(selectedCountry.value)
);

const systemCity = computed(() => locationStore.city);

watch(search, () => {
  if (search.value.length > 0) {
    locationStore.searchCities({
      country: selectedCountry.value,
      q: search.value,
    });
  }
});

watch(selectedCountry, () => {
  if (search.value.length > 0) {
    locationStore.searchCities({
      country: selectedCountry.value,
      q: search.value,
    });
  }
});

onMounted(() => {
  showModal.value = true;
  selectedCountry.value = getSystemCountry();
});

onBeforeMount(() => {
  locationStore.loadCountries();
});
</script>

<template>
  <UiModal
    v-model="showModal"
    :title="t('header.userCity')"
    size="m"
    @update:modelValue="emit('close')"
  >
    <template #default>
      <div class="cities-modal">
        <div class="city-search">
          <UiInput
            v-model="search"
            :placeholder="t('system.search')"
            data-test-id="city-input-search"
          >
            <template #icon-left>
              <IconSearchRegular />
            </template>
          </UiInput>
        </div>

        <div class="countries">
          <UiChip
            v-for="(country, index) in systemCountries"
            :key="index"
            size="m"
            :label="country.name"
            :active="country.id === selectedCountry"
            @click="selectedCountry = country.id"
          >
            <component :is="country.icon" />
          </UiChip>
        </div>

        <div v-if="!search.length" class="cities">
          <button
            v-for="(city, index) in topCities"
            :key="index"
            class="city"
            :class="{ city_select: city.id === systemCity.id }"
            type="button"
            @click="selectUserCity(city)"
          >
            {{ city.title }}
          </button>
        </div>

        <div v-else class="cities-search">
          <div v-if="searchStatus === 'loading'" class="cities-search__loader">
            <span class="icon main-loader" />
          </div>

          <template v-else>
            <div v-if="searchResults.length" class="cities-search__results">
              <button
                v-for="(city, index) in searchResults"
                :key="index"
                class="city"
                :class="{ city_select: city.id === systemCity.id }"
                type="button"
                @click="selectUserCity(city)"
              >
                {{ city.title }}
              </button>
            </div>

            <p v-else>{{ t("header.notFound") }}</p>
          </template>
        </div>
      </div>
    </template>
  </UiModal>
</template>

<style scoped lang="scss">
.cities-modal {
  min-height: 440px;
}

.countries {
  margin: calc($base-space * 6) 0 0;
  display: flex;
  align-items: center;
  justify-items: flex-start;
  gap: calc($base-space * 2);
  max-width: 482px;
}

.country {
  display: flex;
  align-items: center;
  justify-items: flex-start;
  gap: $base-space;
  @include body-l();
  color: var(--color-semantic-content-normal-secondary);
  &_select {
    @include body-bold-l();
    color: var(--color-semantic-content-normal-primary);
  }

  > span {
    width: 28px;
    height: 20px;
  }
}

.cities {
  max-width: 482px;
  margin: calc($base-space * 4) 0 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: calc($base-space / 2) ($base-space * 2);
}

.city {
  @include body-s();
  color: var(--color-semantic-content-normal-primary);
  padding: calc($base-space * 2) ($base-space * 4);
  text-align: left;
  position: relative;

  &_select {
    &:after {
      content: "";
      width: 4px;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      border-radius: 0 4px 4px 0;
      background-color: var(--color-semantic-background-normal-brand);
    }
  }
}

.cities-search {
  margin: calc($base-space * 4) 0 0;

  &__loader {
    display: flex;
    align-items: center;
    justify-content: center;

    > span {
      width: 52px;
      height: 52px;
      background-color: var(--color-semantic-content-normal-primary);
    }
  }

  &__results {
    display: grid;
    grid-template-columns: 1fr;
    gap: calc($base-space / 2);
  }

  > p {
    color: var(--color-semantic-content-normal-tertiary);
    @include body-m();
  }
}
</style>
